import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import {
  BehaviorSubject, catchError,
  concatMap,
  delay,
  filter,
  finalize,
  from,
  Observable, of,
  take,
} from 'rxjs';
import { NavController } from '@ionic/angular';

@Injectable({ providedIn: 'root' })
export class NavigationService {
  navEvents$: Observable<NavigationEnd>;
  currentUrl = '';

  private curtainId = 'ion-pull-refresh-curtain';
  private readonly navEventsSource$ = new BehaviorSubject<NavigationEnd>(undefined);

  constructor(
    private readonly navCtrl: NavController,
    private readonly router: Router,
  ) {
    this.navEvents$ = this.navEventsSource$.asObservable();
    this.removeCurtain();

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.addEvent(event));

    this.navEvents$
      .pipe(
        catchError((err) => {
         console.log(err);

         return of(err);
        })
      )
      .subscribe()
  }

  refresh(toUrl?: string): void {
    this.createCurtain();
    const url = toUrl ? toUrl : this.currentUrl;

    from(this.navCtrl.navigateBack('/root', { animated: false }))
      .pipe(
        concatMap(() => from(this.navCtrl.navigateForward(url, { animated: false }))),
        take(1),
        delay(1000),
        finalize(() => {
          this.removeCurtain();
          this.clearSelection();
        }),
      )
      .subscribe();
  }

  addEvent(navEvent: NavigationEnd): void {
    this.currentUrl = navEvent.url;
    this.navEventsSource$.next(navEvent);
  }

  private clearSelection(): void {
    let sel;
    if ((sel = (document as any).selection) && sel.empty) {
      sel.empty();
    } else {
      if (window.getSelection) {
        window.getSelection().removeAllRanges();
      }
      const activeEl = document.activeElement;
      if (activeEl) {
        const tagName = activeEl.nodeName.toLowerCase();
        if (tagName === 'textarea' ||
          (tagName === 'input' && (activeEl as any).type === 'text')) {
          // Collapse the selection to the end
          (activeEl as any).selectionStart = (activeEl as any).selectionEnd;
        }
      }
    }
  }

  private createCurtain(): void {
    const curtain = document.createElement('div');
    curtain.id = this.curtainId;
    curtain.classList.add('background-background');
    curtain.style.cssText = `
    position:fixed;
    width:100vw;
    height:100vh;
    z-index:100;
    display: flex;
    justify-content: center;
    align-items: center;`;

    const loaderText = document.createElement('p');
    loaderText.style.cssText = `user-select: none;`;
    loaderText.innerText = 'Loading ...';
    curtain.appendChild(loaderText);

    document.body.appendChild(curtain);
  }

  private removeCurtain(): void {
    const curtain = document.getElementById(this.curtainId);
    if (!curtain) {
      return;
    }

    curtain.remove();
  }
}
